/**
 * search-modal.js
 * Opens and closes the header's search dialog (snippets/search-modal.liquid).
 *
 * Everything here is delegated from `document` and every node is looked up at
 * event time. That is deliberate: the header section is re-rendered wholesale
 * on `shopify:section:load` in the theme editor, so any cached reference to
 * the dialog or to a [data-search-toggle] button becomes a detached node and
 * the control silently stops working. Delegation means there is no re-init
 * path to get wrong — hence the one-time guard rather than an init(root).
 *
 * It also reflects `data-scrolled` onto <predictive-search> whenever the
 * results list is scrolled away from the top, which is what reveals the rule
 * under the search row (see .search-modal__bar::after). Scroll does not
 * bubble, so that listener is bound in the capture phase on document — the one
 * way to catch it from a node this file never holds a reference to.
 *
 * Contract:
 *   [data-search-toggle]  opens the dialog named by its aria-controls
 *   [data-search-close]   closes the dialog it sits inside
 *   dialog#search-modal   the dialog itself (native <dialog>, scroll-lock)
 *   [data-scrolled]       written onto <predictive-search>, read by the CSS
 *   Cmd/Ctrl+K            global toggle
 */
(function () {
  'use strict';

  // Guard the document-level listeners against double-binding. Matches the
  // pattern in hero-anchor-scroll.js.
  if (window.__searchModalInit) return;
  window.__searchModalInit = true;

  var DIALOG_ID = 'search-modal';

  function dialog() {
    var el = document.getElementById(DIALOG_ID);
    return el && typeof el.showModal === 'function' ? el : null;
  }

  function syncToggles(expanded) {
    document.querySelectorAll('[data-search-toggle]').forEach(function (toggle) {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function open() {
    var el = dialog();
    if (!el || el.open) return;
    el.showModal();
    syncToggles(true);
    watchResults(el);
    // Focus after the dialog has been painted, or Safari drops it.
    requestAnimationFrame(function () {
      el.querySelector('[data-predictive-search-input]')?.focus();
    });
  }

  function close() {
    var el = dialog();
    if (el && el.open) el.close();
  }

  function syncScrolled(results) {
    var inner = results.closest('predictive-search');
    if (inner) inner.toggleAttribute('data-scrolled', results.scrollTop > 0);
  }

  // Swapping the results out from under the scroll position clamps scrollTop
  // without a `scroll` event we can count on, which would leave the rule drawn
  // under a panel that is no longer scrolled. Observing the container is
  // deterministic where a timer racing predictive-search.js's debounce is not.
  // Created per open and dropped on close, so it never outlives the node it
  // watches — the same reason nothing else here caches a reference.
  var resultsObserver = null;

  function watchResults(el) {
    var results = el.querySelector('[data-predictive-search-container]');
    if (!results || typeof MutationObserver !== 'function') return;
    resultsObserver = new MutationObserver(function () { syncScrolled(results); });
    resultsObserver.observe(results, { childList: true, subtree: true });
  }

  function unwatchResults() {
    if (resultsObserver) resultsObserver.disconnect();
    resultsObserver = null;
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-search-toggle]');
    if (toggle) {
      event.preventDefault();
      open();
      return;
    }

    if (event.target.closest('[data-search-close]')) {
      close();
      return;
    }

    // Backdrop click: the event target is the dialog itself only when the
    // pointer landed outside its content box.
    if (event.target.id === DIALOG_ID) close();
  });

  // `close` fires for every close path — button, backdrop, and the native
  // Escape handling that <dialog> gives us for free.
  document.addEventListener(
    'close',
    function (event) {
      if (event.target.id !== DIALOG_ID) return;
      syncToggles(false);
      unwatchResults();
      event.target.querySelector('predictive-search')?.removeAttribute('data-scrolled');
    },
    true
  );

  // Capture phase: `scroll` does not bubble, so a delegated listener only ever
  // sees it on the way down.
  document.addEventListener(
    'scroll',
    function (event) {
      var results = event.target;
      if (!results || results.nodeType !== 1 || typeof results.matches !== 'function') return;
      if (!results.matches('[data-predictive-search-container]')) return;
      syncScrolled(results);
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      var el = dialog();
      if (!el) return;
      event.preventDefault();
      el.open ? close() : open();
    }
  });
})();
